import { useState, type FormEvent, type ReactNode } from 'react';
import {
  SPLICE_KIND_LABELS,
  SPLITTER_CAPACITIES,
  SPLITTER_KIND_LABELS,
  type OpticalSplitter,
  type SpliceKind,
  type Splitter,
  type SplitterKind,
} from '@simpleacs/shared';
import { Button, Field, Input } from '../components/ui';
import { api } from '../lib/api';
import { useTasks } from '../lib/tasks';

/**
 * Create/edit for a physical splitter device — shared by the Splitters page
 * and the topology tree, so the registry and the tree can never drift apart.
 *
 * The upstream block is the point of the whole entity: what the tree cannot
 * select. A splice either points at another device, or at a fiber core (drop
 * cable or ADSS) that only the comment can name — colour, tray, loose tube.
 *
 * Submitting closes the dialog at once and hands the write to the tasks tray;
 * the topology query refreshes when it lands.
 */
export function OpticalSplitterDialog({
  existing,
  defaultHousingId,
  housings,
  devices,
  onClose,
}: {
  existing: OpticalSplitter | null;
  defaultHousingId?: string | null;
  housings: Splitter[];
  devices: OpticalSplitter[];
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [form, setForm] = useState({
    name: existing?.name ?? '',
    outputs: String(existing?.outputs ?? 8),
    housingId: existing?.housingId ?? defaultHousingId ?? '',
    upstreamKind: (existing?.upstreamKind ?? '') as '' | SpliceKind,
    upstreamSplitterId: existing?.upstreamSplitterId ?? '',
    upstreamComment: existing?.upstreamComment ?? '',
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Snapshotted: the dialog is gone by the time the write settles.
    const body = {
      name: form.name.trim(),
      outputs: Number(form.outputs) || 8,
      housingId: form.housingId || null,
      upstreamKind: form.upstreamKind || null,
      upstreamSplitterId:
        form.upstreamKind === 'splitter' ? form.upstreamSplitterId || null : null,
      upstreamComment: form.upstreamComment.trim() || null,
    };
    onClose();
    tasks.start({
      label: existing ? `Save splitter ${existing.name}` : `Create splitter ${body.name}`,
      run: () =>
        existing
          ? api.patch(`/optical-splitters/${encodeURIComponent(existing.id)}`, body)
          : api.post('/optical-splitters', body),
      done: () => (existing ? `${body.name} saved.` : `${body.name} created.`),
      invalidate: [['topology']],
    });
  };

  const spliceTargets = devices.filter((d) => d.id !== existing?.id);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <form
        onSubmit={onSubmit}
        className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl"
      >
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">
            {existing ? `Edit ${existing.name}` : 'New splitter'}
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            The physical 1:N device — a housing holds any number of them.
          </p>
        </header>
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <Field label="Name">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="SPL-WB-03"
              required
              autoFocus
            />
          </Field>
          <Field label="Ratio">
            <select
              className="input"
              value={form.outputs}
              onChange={(e) => setForm({ ...form, outputs: e.target.value })}
            >
              {SPLITTER_CAPACITIES.map((n) => (
                <option key={n} value={n}>
                  1:{n}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Housed in">
            <select
              className="input"
              value={form.housingId}
              onChange={(e) => setForm({ ...form, housingId: e.target.value })}
            >
              <option value="">Not housed yet</option>
              {housings.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name} — {SPLITTER_KIND_LABELS[h.kind as SplitterKind]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Upstream splice">
            <select
              className="input"
              value={form.upstreamKind}
              onChange={(e) =>
                setForm({ ...form, upstreamKind: e.target.value as '' | SpliceKind })
              }
            >
              <option value="">Straight to the parent / unrecorded</option>
              {(Object.keys(SPLICE_KIND_LABELS) as SpliceKind[]).map((k) => (
                <option key={k} value={k}>
                  {SPLICE_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </Field>
          {form.upstreamKind === 'splitter' && (
            <Field label="Spliced to">
              <select
                className="input"
                value={form.upstreamSplitterId}
                onChange={(e) => setForm({ ...form, upstreamSplitterId: e.target.value })}
                required
              >
                <option value="">Choose a splitter…</option>
                {spliceTargets.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} (1:{d.outputs})
                  </option>
                ))}
              </select>
            </Field>
          )}
          <div className={form.upstreamKind === 'splitter' ? 'sm:col-span-2' : 'sm:col-span-2'}>
            <Field label="Upstream comment">
              <textarea
                className="input min-h-[64px]"
                value={form.upstreamComment}
                onChange={(e) => setForm({ ...form, upstreamComment: e.target.value })}
                placeholder="What the tree cannot say — “blue ADSS core, tray 2”, “drop cable from pole 14”…"
                maxLength={500}
              />
            </Field>
          </div>
        </div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3.5">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            {existing ? 'Save' : 'Create'}
          </Button>
        </footer>
      </form>
    </div>
  );
}

/**
 * Small shared confirm-delete. Confirming closes it at once and the delete
 * runs in the tasks tray, which is where the server's refusal — "spliced to
 * by name", "still houses splitters" — is kept until it is read.
 */
export function ConfirmDeleteDialog({
  title,
  body,
  onConfirm,
  onClose,
  outcome,
}: {
  /** "Delete FAT-12?" — also the tray's label, question mark dropped. */
  title: string;
  body: ReactNode;
  /** The delete itself; any cache refresh it needs can be awaited inside it. */
  onConfirm: () => Promise<unknown>;
  onClose: () => void;
  /** What the toast says when it worked. Defaults to "<name> deleted." */
  outcome?: string;
}) {
  const tasks = useTasks();
  const label = title.replace(/\?\s*$/, '');
  const confirm = () => {
    onClose();
    tasks.start({
      label,
      run: onConfirm,
      done: () => outcome ?? `${label.replace(/^Delete\s+/i, '')} deleted.`,
    });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
        </header>
        <div className="px-5 py-4 text-sm text-muted">{body}</div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3.5">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="danger" onClick={confirm}>
            Delete
          </Button>
        </footer>
      </div>
    </div>
  );
}

/** One line for where a device's light comes from, comment included. */
export function upstreamSummary(
  device: OpticalSplitter,
  all: OpticalSplitter[],
): string | null {
  const comment = device.upstreamComment ? ` — ${device.upstreamComment}` : '';
  if (device.upstreamKind === 'splitter') {
    const target = all.find((d) => d.id === device.upstreamSplitterId);
    return `Spliced to ${target?.name ?? 'a removed splitter'}${comment}`;
  }
  if (device.upstreamKind === 'drop_cable') return `Drop cable core${comment}`;
  if (device.upstreamKind === 'adss') return `ADSS core${comment}`;
  return device.upstreamComment ? device.upstreamComment : null;
}
